// PWA Push Notifications Utility
export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
  }>;
  requireInteraction?: boolean;
  silent?: boolean;
  vibrate?: number[];
  url?: string;
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

// 브라우저 푸시 알림 지원 확인
export const isPushNotificationSupported = (): boolean => {
  // 서버 사이드 렌더링 환경에서는 false 반환
  if (typeof window === 'undefined') {
    return false;
  }
  
  // 필수 API 지원 확인
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  
  // HTTPS 환경 확인 (localhost는 예외)
  const isSecureContext = window.isSecureContext || 
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1';
  
  // iOS Safari는 PWA 설치(standalone) 여부를 추가 확인 (지원 여부는 별도로 안내)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

  const isSupported = hasServiceWorker && hasPushManager && hasNotification && isSecureContext;
  if (isIOSSafari && !isStandalone) {
    console.warn('⚠️ [iOS Safari] PWA가 설치되지 않음 - 홈 화면에 추가 필요');
    return false;
  }
  
  if (!isSupported) {
    console.warn('⚠️ [푸시 알림] 지원되지 않는 환경:', {
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname
    });
  }
  
  return isSupported;
};

// 알림 권한 확인
export const getNotificationPermission = (): NotificationPermission => {
  if (!isPushNotificationSupported()) {
    return 'denied';
  }
  
  try {
    return Notification.permission;
  } catch (error) {
    console.error('❌ [알림 권한] 권한 확인 실패:', error);
    return 'denied';
  }
};

// 알림 권한 요청
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported in this environment');
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('🔔 [알림 권한] 요청 결과:', permission);
    return permission;
  } catch (error) {
    console.error('❌ [알림 권한] 요청 실패:', error);
    throw new Error('Failed to request notification permission');
  }
};

// Service Worker 등록 (Firebase Service Worker 사용) - 오류 처리 강화
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker is not supported');
  }

  try {
    console.log('🔄 [Service Worker] Service Worker 등록 시작...');
    console.log('🔍 [Service Worker] 현재 URL:', window.location.href);
    console.log('🔍 [Service Worker] 현재 프로토콜:', window.location.protocol);
    
    // iOS Safari 감지
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      console.log('🍎 [iOS Safari] Service Worker 등록 - iOS Safari 전용 처리');
      console.log('🍎 [iOS Safari] iOS 버전:', navigator.userAgent.match(/OS (\d+)_/)?.[1] || 'Unknown');
    }

    // 기존 등록 확인 (동일 스크립트면 재사용)
    console.log('🧹 [Service Worker] 기존 Service Worker 확인 중...');
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    if (existingRegistration?.active?.scriptURL?.includes('firebase-messaging-sw.js')) {
      console.log('✅ [Service Worker] 기존 Firebase Service Worker 재사용');
      return existingRegistration;
    }

    console.log('🔄 [Service Worker] Firebase Service Worker 등록 시도...');
    
    // Firebase Service Worker 등록 시도
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/'
    });

    console.log('✅ [Service Worker] Service Worker 등록 성공');
    console.log('🔍 [Service Worker] 등록된 Service Worker:', {
      scope: registration.scope,
      installing: registration.installing?.state,
      waiting: registration.waiting?.state,
      active: registration.active?.state
    });

    // Service Worker 활성화 대기 (모든 플랫폼)
    console.log('⏳ [Service Worker] 활성화 대기 중...');
    
    // 이미 활성화되어 있으면 즉시 종료
    if (registration.active && registration.active.state === 'activated') {
      console.log('✅ [Service Worker] 이미 활성화되어 있음 - 즉시 진행');
    } else {
      let activationWaitCount = 0;
      const maxActivationWaits = 10; // 5초 (500ms * 10) - 단축됨
      
      while (activationWaitCount < maxActivationWaits) {
        // 현재 상태 확인
        if (registration.active && registration.active.state === 'activated') {
          console.log('✅ [Service Worker] 활성화 완료');
          break;
        }
        
        // installing 상태인 경우
        if (registration.installing) {
          console.log(`⏳ [Service Worker] 설치 중... (${registration.installing.state})`);
        }
        
        // waiting 상태인 경우 활성화 요청
        if (registration.waiting && registration.waiting.state === 'installed') {
          console.log('🔄 [Service Worker] waiting 상태 - 활성화 요청');
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        
        // 모든 플랫폼 동일한 대기 시간 (500ms)
        await new Promise(resolve => setTimeout(resolve, 500));
        activationWaitCount++;
      }
    }

    // Service Worker 상태 확인
    console.log('🔍 [Service Worker] 활성화 후 상태 확인...');
    console.log('🔍 [Service Worker] installing:', registration.installing?.state);
    console.log('🔍 [Service Worker] waiting:', registration.waiting?.state);
    console.log('🔍 [Service Worker] active:', registration.active?.state);
    
    // 최종 활성화 확인
    if (!registration.active || registration.active.state !== 'activated') {
      console.warn('⚠️ [Service Worker] 활성화되지 않음 - ready Promise로 재확인');
      // ready Promise로 재확인
      const readyRegistration = await navigator.serviceWorker.ready;
      if (!readyRegistration.active || readyRegistration.active.state !== 'activated') {
        throw new Error('Service Worker가 활성화되지 않았습니다. Registration does not have an active worker');
      }
      return readyRegistration;
    }

    // Service Worker 업데이트 체크 (새로고침 방지)
    registration.addEventListener('updatefound', () => {
      console.log('🔄 [Service Worker] 업데이트 발견');
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          console.log('🔄 [Service Worker] 새 Worker 상태 변경:', newWorker.state);
          if (newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // 새 Service Worker 설치됨 (업데이트)
              console.log('New Service Worker installed. Skipping reload to preserve push subscription...');
              // 새로고침 대신 즉시 활성화
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          }
        });
      }
    });

    // Service Worker가 완전히 준비될 때까지 대기
    console.log('⏳ [Service Worker] Service Worker 준비 대기...');
    const finalRegistration = await navigator.serviceWorker.ready;
    
    // 최종 활성화 상태 확인
    if (!finalRegistration.active || finalRegistration.active.state !== 'activated') {
      console.warn('⚠️ [Service Worker] ready Promise 반환했지만 활성화되지 않음');
      console.warn('⚠️ [Service Worker] 상태:', {
        active: finalRegistration.active?.state,
        installing: finalRegistration.installing?.state,
        waiting: finalRegistration.waiting?.state
      });
      
      // 추가 대기 후 재확인
      await new Promise(resolve => setTimeout(resolve, 2000));
      const retryRegistration = await navigator.serviceWorker.ready;
      
      if (!retryRegistration.active || retryRegistration.active.state !== 'activated') {
        throw new Error('Service Worker가 활성화되지 않았습니다. Registration does not have an active worker');
      }
      
      console.log('✅ [Service Worker] 재확인 후 활성화 확인 완료');
      return retryRegistration;
    }
    
    console.log('✅ [Service Worker] Service Worker 활성화 완료');
    console.log('🔍 [Service Worker] 최종 상태:', {
      scope: finalRegistration.scope,
      active: finalRegistration.active.state,
      scriptURL: finalRegistration.active.scriptURL
    });
    
    // PushManager 지원 확인
    if (!finalRegistration.pushManager) {
      throw new Error('PushManager is not supported in this Service Worker');
    }
    
    console.log('✅ [Service Worker] PushManager 지원 확인됨');
    return finalRegistration;
  } catch (error) {
    console.error('❌ [Service Worker] Service Worker 등록 실패:', error);
    
    throw new Error('Service Worker 등록에 실패했습니다. 네트워크 연결을 확인하고 다시 시도해주세요.');
  }
};

// iOS Safari 전용 구독 생성 (iOS 16.4+ Web Push API 사용)
const createIOSSafariSubscription = async (vapidPublicKey: string, forceNew: boolean = false): Promise<PushSubscription | any> => {
  console.log('🍎 [iOS Safari] iOS Safari Web Push API 구독 생성 시작...', forceNew ? '(강제 새 구독)' : '');
  
  try {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    if (!isStandalone) {
      throw new Error('PWA로 홈 화면에 추가된 상태에서만 iOS Web Push를 사용할 수 있습니다.');
    }
    // iOS 16.4+에서는 Web Push API 지원 (PWA로 홈 화면에 추가된 경우)
    // Service Worker 등록 시도
    console.log('🍎 [iOS Safari] Service Worker 등록 시도 (iOS 16.4+ Web Push API)');
    
    try {
      // Service Worker 등록 먼저 시도
      await registerServiceWorker();
      console.log('✅ [iOS Safari] Service Worker 등록 완료');
      
      // Service Worker가 완전히 활성화될 때까지 대기
      let registration = await navigator.serviceWorker.ready;
      
      // 이미 활성화되어 있으면 즉시 진행
      if (registration.active && registration.active.state === 'activated') {
        console.log('✅ [iOS Safari] Service Worker 이미 활성화되어 있음 - 즉시 진행');
      } else {
        // 활성화 확인 (최대 5초 대기) - 단축됨
        let retryCount = 0;
        const maxRetries = 10; // 5초 (500ms * 10)
        while (retryCount < maxRetries) {
          registration = await navigator.serviceWorker.ready;
          
          if (registration.active && registration.active.state === 'activated') {
            console.log('✅ [iOS Safari] Service Worker 활성화 확인 완료');
            break;
          }
          
          // waiting 상태인 경우 활성화 요청
          if (registration.waiting && registration.waiting.state === 'installed') {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            console.log('🔄 [iOS Safari] Service Worker 활성화 요청 (SKIP_WAITING)');
          }
          
          console.log(`⏳ [iOS Safari] Service Worker 활성화 대기 중... (${retryCount + 1}/${maxRetries}) - 상태: ${registration.active?.state || registration.installing?.state || registration.waiting?.state || 'unknown'}`);
          await new Promise(resolve => setTimeout(resolve, 500));
          retryCount++;
        }
      }
      
      // 활성화 상태 최종 확인
      registration = await navigator.serviceWorker.ready;
      if (!registration.active || registration.active.state !== 'activated') {
        console.warn('⚠️ [iOS Safari] Service Worker 활성화 확인 실패 - 폴백 경로로 진행');
        const fallbackRegistration = await navigator.serviceWorker.getRegistration('/') || registration;
        registration = fallbackRegistration || registration;
      } else {
        console.log('✅ [iOS Safari] Service Worker 활성화 확인 완료:', {
          state: registration.active.state,
          scriptURL: registration.active.scriptURL
        });
      }
      
      // PushManager 사용 가능 여부 확인
      if (!registration.pushManager) {
        console.warn('⚠️ [iOS Safari] PushManager 사용 불가 - PWA로 홈 화면에 추가되지 않았을 수 있음');
        throw new Error('PushManager not available - PWA may not be installed');
      }
      
      console.log('✅ [iOS Safari] PushManager 사용 가능 - Web Push API 사용');
      
      // 강제 새 구독 모드이거나 사용자가 알림을 비활성화했다가 다시 활성화하는 경우
      if (forceNew || localStorage.getItem('user_disabled_subscription') === 'true') {
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
          console.log('🔄 [iOS Safari] 기존 구독 발견 - 강제 해제 후 새로 생성:', existingSubscription.endpoint);
          try {
            await existingSubscription.unsubscribe();
            console.log('✅ [iOS Safari] 기존 구독 해제 완료');
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (error) {
            console.warn('⚠️ [iOS Safari] 기존 구독 해제 실패 (계속 진행):', error);
          }
        }
      } else {
        // 기존 구독 확인
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
          console.log('✅ [iOS Safari] 기존 구독 발견:', existingSubscription.endpoint);
          return existingSubscription;
        }
      }
      
      // 새 구독 생성
      console.log('🔄 [iOS Safari] 새 구독 생성 중...');
      const subscribeWithRetry = async (): Promise<PushSubscription> => {
        return registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
      };

      let subscription: PushSubscription;
      try {
        subscription = await subscribeWithRetry();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isActiveWorkerError = errorMessage.includes('active worker') ||
          errorMessage.includes('No active Service Worker') ||
          errorMessage.includes('no active service worker');

        if (isActiveWorkerError) {
          console.warn('⚠️ [iOS Safari] 활성 SW 없음 오류 - 재시도 진행:', errorMessage);
          await new Promise(resolve => setTimeout(resolve, 1000));
          const readyRegistration = await navigator.serviceWorker.ready;
          if (readyRegistration) {
            registration = readyRegistration;
          }
          subscription = await subscribeWithRetry();
        } else {
          throw error;
        }
      }
      
      console.log('✅ [iOS Safari] Web Push API 구독 생성 성공:', subscription.endpoint);
      return subscription;
    } catch (swError) {
      console.warn('⚠️ [iOS Safari] Service Worker/PushManager 사용 실패:', swError);
      throw swError;
    }
  } catch (error) {
    console.error('❌ [iOS Safari] 구독 생성 실패:', error);
    throw new Error(`iOS Safari 구독 생성 실패: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// 푸시 구독 생성 (iOS Safari 전용 처리 포함)
export const subscribeToPush = async (
  vapidPublicKey: string,
  forceNew: boolean = false
): Promise<PushSubscription | any> => {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported');
  }

  console.log('🔄 [푸시 구독] 푸시 구독 생성 시작...', forceNew ? '(강제 새 구독)' : '');
  
  // iOS Safari 감지
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;
  
  if (isIOSSafari) {
    console.log('🍎 [iOS Safari] 푸시 구독 생성 - iOS 16.4+ Web Push API 시도');
    return await createIOSSafariSubscription(vapidPublicKey, forceNew);
  }

  // 일반 브라우저 (Chrome, Firefox 등) 처리
  console.log('🌐 [일반 브라우저] Web Push API 사용');

  // Service Worker 등록 및 활성화 보장
  let registration: ServiceWorkerRegistration;
  
  // 재활성화 시 기존 Service Worker가 비활성화 상태일 수 있으므로 새로 등록
  // 재활성화 시 강제 새 구독 모드 확인
  const wasDisabled = localStorage.getItem('user_disabled_subscription') === 'true';
  const shouldForceNew = forceNew || wasDisabled;
  
  if (shouldForceNew) {
    console.log('🔄 [푸시 구독] 재활성화 모드 - Service Worker 새로 등록');
    
    // 기존 Service Worker 완전히 제거
    try {
      const existingRegistrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of existingRegistrations) {
        if (reg.scope === '/' && reg.active?.scriptURL?.includes('firebase-messaging-sw.js')) {
          console.log('🗑️ [푸시 구독] 기존 Service Worker 제거:', reg.active.scriptURL);
          await reg.unregister();
          // 제거 완료 대기
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    } catch (error) {
      console.warn('⚠️ [푸시 구독] 기존 Service Worker 제거 실패:', error);
    }
  }

  // Service Worker 등록
  try {
    registration = await registerServiceWorker();
    console.log('✅ [푸시 구독] Service Worker 등록 완료');
  } catch (swError) {
    console.warn('⚠️ [푸시 구독] Service Worker 등록 실패, 기존 등록 확인:', swError);
    // 등록 실패 시 기존 등록 시도
    try {
      registration = await navigator.serviceWorker.ready;
    } catch (error) {
      throw new Error('Service Worker를 등록할 수 없습니다. Registration does not have an active worker');
    }
  }

  // Service Worker가 완전히 활성화될 때까지 명시적으로 대기
  console.log('⏳ [푸시 구독] Service Worker 활성화 대기...');
  
  // 현재 등록 상태 확인
  registration = await navigator.serviceWorker.ready;
  
  // 이미 활성화되어 있으면 즉시 진행
  if (registration.active && registration.active.state === 'activated') {
    console.log('✅ [푸시 구독] Service Worker 이미 활성화되어 있음 - 즉시 진행');
  } else {
    // 활성화 상태 확인 및 대기 (최대 5초) - 단축됨
    let activationRetryCount = 0;
    const maxActivationRetries = 10; // 5초 (500ms * 10)
    
    while (activationRetryCount < maxActivationRetries) {
      // 현재 등록 상태 다시 가져오기
      registration = await navigator.serviceWorker.ready;
      
      // 활성화 상태 확인
      if (registration.active && registration.active.state === 'activated') {
        console.log('✅ [푸시 구독] Service Worker 활성화 확인 완료');
        break;
      }
      
      // installing 또는 waiting 상태인 경우
      if (registration.installing) {
        console.log(`⏳ [푸시 구독] Service Worker 설치 중... (${registration.installing.state})`);
      } else if (registration.waiting) {
        console.log(`⏳ [푸시 구독] Service Worker 대기 중... (${registration.waiting.state})`);
        // waiting 상태인 경우 활성화 요청
        if (registration.waiting.state === 'installed') {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          console.log('🔄 [푸시 구독] Service Worker 활성화 요청 (SKIP_WAITING)');
        }
      } else if (registration.active) {
        console.log(`⏳ [푸시 구독] Service Worker 활성화 대기 중... (${registration.active.state})`);
      } else {
        console.log('⏳ [푸시 구독] Service Worker 상태 확인 중...');
      }
      
      activationRetryCount++;
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
  
  // 최종 활성화 상태 확인
  registration = await navigator.serviceWorker.ready;
  if (!registration.active || registration.active.state !== 'activated') {
    console.error('❌ [푸시 구독] Service Worker 활성화 실패:', {
      active: registration.active?.state,
      installing: registration.installing?.state,
      waiting: registration.waiting?.state
    });
    throw new Error('Service Worker가 활성화되지 않았습니다. Registration does not have an active worker');
  }
  
  console.log('✅ [푸시 구독] Service Worker 활성화 확인 완료:', {
    scope: registration.scope,
    active: registration.active.state,
    scriptURL: registration.active.scriptURL
  });
  
  if (!registration.pushManager) {
    console.error('❌ [푸시 구독] PushManager를 사용할 수 없습니다');
    throw new Error('Push manager unavailable');
  }
  
  console.log('✅ [푸시 구독] PushManager 사용 가능 확인됨');

  // 기존 구독 확인
  console.log('🔍 [푸시 구독] 기존 구독 확인 중...');
  const existingSubscription = await registration.pushManager.getSubscription();
  
  // 재활성화 시 강제 새 구독 모드 확인 (위에서 이미 선언된 shouldForceNew 재사용)
  
  // 강제 새 구독 모드이거나 사용자가 알림을 비활성화했다가 다시 활성화하는 경우
  if (shouldForceNew) {
    if (existingSubscription) {
      console.log('🔄 [푸시 구독] 기존 구독 발견 - 강제 해제 후 새로 생성:', existingSubscription.endpoint);
      try {
        await existingSubscription.unsubscribe();
        console.log('✅ [푸시 구독] 기존 구독 해제 완료');
        // 구독 해제 후 브라우저가 완전히 처리할 때까지 대기
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // 해제 확인
        const verifySubscription = await registration.pushManager.getSubscription();
        if (verifySubscription) {
          console.warn('⚠️ [푸시 구독] 구독이 여전히 존재함 - 재시도');
          await verifySubscription.unsubscribe();
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.warn('⚠️ [푸시 구독] 기존 구독 해제 실패 (계속 진행):', error);
      }
    } else {
      console.log('ℹ️ [푸시 구독] 기존 구독 없음 - 새 구독 생성');
    }
  } else if (existingSubscription) {
    console.log('✅ [푸시 구독] 기존 구독 발견:', existingSubscription.endpoint);
    return existingSubscription;
  } else {
    console.log('ℹ️ [푸시 구독] 기존 구독 없음 - 새 구독 생성');
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  console.log('✅ [푸시 구독] 새 구독 생성 성공:', subscription.endpoint);
  return subscription;
};

// 푸시 구독 해제
export const unsubscribeFromPush = async (): Promise<boolean> => {
  if (!isPushNotificationSupported()) {
    return false;
  }

  // iOS Safari 더미 구독 객체 확인
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;

  try {
    // iOS Safari 더미 구독 객체는 Service Worker 없이도 처리 가능
    if (isIOSSafari && !navigator.serviceWorker) {
      console.log('ℹ️ [푸시 구독 해제] iOS Safari 더미 구독 객체 - Service Worker 없음, 해제 완료로 처리');
      return true;
    }
    
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager?.getSubscription();
    
    if (subscription) {
      // 더미 구독 객체 확인 (iOS Safari 폴백 모드)
      if (subscription.endpoint === 'https://apple-push-endpoint.com' || 
          (subscription as any).fallbackMode === true ||
          (subscription as any).serviceWorkerSkipped === true) {
        console.log('ℹ️ [푸시 구독 해제] iOS Safari 더미 구독 객체 - 해제 완료로 처리');
        return true;
      }
      
      console.log('🔄 [푸시 구독 해제] 브라우저 구독 해제 시작:', subscription.endpoint);
      const result = await subscription.unsubscribe();
      console.log('✅ [푸시 구독 해제] 브라우저 구독 해제 완료:', result);
      
      // 구독 해제 후 브라우저가 완전히 처리할 때까지 대기
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 해제 확인
      const verifySubscription = await registration.pushManager?.getSubscription();
      if (verifySubscription) {
        console.warn('⚠️ [푸시 구독 해제] 구독이 여전히 존재함 - 재시도');
        // 재시도
        await verifySubscription.unsubscribe();
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.log('✅ [푸시 구독 해제] 구독 해제 확인 완료');
      }
      
      return result;
    } else {
      console.log('ℹ️ [푸시 구독 해제] 구독이 이미 없음');
      return true;
    }
  } catch (error) {
    console.error('❌ [푸시 구독 해제] 구독 해제 실패:', error);
    
    // iOS Safari 더미 구독 객체인 경우 오류 무시
    if (isIOSSafari) {
      console.log('ℹ️ [푸시 구독 해제] iOS Safari 더미 구독 객체 오류 무시');
      return true;
    }
    
    return false;
  }
};

// 로컬 알림 표시
export const showLocalNotification = async (
  options: NotificationOptions
): Promise<void> => {
  if (!isPushNotificationSupported()) {
    throw new Error('Notifications are not supported');
  }

  const permission = getNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission not granted');
  }

  const registration = await navigator.serviceWorker.ready;
  
  const isAndroid = /Android/i.test(navigator.userAgent);
  const defaultIcon = isAndroid ? '/logo192android.png' : '/logo192.png';
  const notificationOptions = {
    body: options.body,
    icon: options.icon || defaultIcon,
    badge: options.badge || defaultIcon,
    tag: options.tag || 'judam-notification',
    data: options.data || {},
    actions: options.actions || [],
    requireInteraction: options.requireInteraction || false,
    silent: options.silent || false,
    vibrate: options.vibrate || [200, 100, 200]
  };

  await registration.showNotification(options.title, notificationOptions);
};

// Base64 URL을 Uint8Array로 변환 (PushSubscription 호환 타입)
export const urlBase64ToUint8Array = (base64String: string): ArrayBuffer => {
  try {
    console.log('🔍 [VAPID] 변환 시작 - 키 길이:', base64String.length);
    console.log('🔍 [VAPID] 전체 키:', base64String);
    
    // 입력 검증
    if (!base64String || typeof base64String !== 'string') {
      throw new Error('Invalid VAPID key: not a string');
    }
    
    // Base64 URL 형식 검증
    const base64Regex = /^[A-Za-z0-9_-]+$/;
    if (!base64Regex.test(base64String)) {
      throw new Error('Invalid VAPID key: contains invalid characters');
    }
    
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    console.log('🔍 [VAPID] 패딩 추가 후:', base64.length, '자');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    console.log('✅ [VAPID] 변환 성공:', outputArray.length, 'bytes');
    return outputArray.buffer;
  } catch (error) {
    console.error('❌ [VAPID] 변환 실패:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`VAPID key conversion failed: ${errorMessage}`);
  }
};

// 푸시 구독 데이터를 서버 전송 형식으로 변환
export const serializePushSubscription = (
  subscription: PushSubscription | any
): PushSubscriptionData => {
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
      auth: arrayBufferToBase64(subscription.getKey('auth'))
    }
  };
};

// ArrayBuffer를 Base64URL로 변환 (Web Push 호환)
const arrayBufferToBase64 = (buffer: ArrayBuffer | null): string => {
  if (!buffer) return '';
  
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  const base64 = window.btoa(binary);
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

// 알림 타입별 기본 설정
export const getNotificationConfig = (type: string): Partial<NotificationOptions> => {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const defaultIcon = isAndroid ? '/logo192android.png' : '/logo192.png';
  const configs = {
    // 재고 부족 알림
    lowStock: {
      icon: defaultIcon,
      badge: defaultIcon,
      tag: 'low-stock',
      requireInteraction: true,
      vibrate: [300, 100, 300, 100, 300],
      actions: [
        { action: 'view', title: '확인하기' },
        { action: 'order', title: '발주하기' }
      ]
    },
    // 발주 가능 알림
    orderAvailable: {
      icon: defaultIcon,
      badge: defaultIcon,
      tag: 'order-available',
      requireInteraction: false,
      vibrate: [200, 100, 200],
      actions: [
        { action: 'view', title: '확인하기' },
        { action: 'order', title: '발주하기' }
      ]
    },
    // 발주 마감 알림
    orderDeadline: {
      icon: defaultIcon,
      badge: defaultIcon,
      tag: 'order-deadline',
      requireInteraction: true,
      vibrate: [500, 200, 500],
      actions: [
        { action: 'view', title: '확인하기' },
        { action: 'order', title: '지금 발주' }
      ]
    },
    // 입고 완료 알림
    inboundComplete: {
      icon: defaultIcon,
      badge: defaultIcon,
      tag: 'inbound-complete',
      requireInteraction: false,
      vibrate: [200, 100, 200],
      actions: [
        { action: 'view', title: '확인하기' }
      ]
    },
    // 시스템 알림
    system: {
      icon: defaultIcon,
      badge: defaultIcon,
      tag: 'system',
      requireInteraction: false,
      vibrate: [200],
      actions: [
        { action: 'view', title: '확인하기' }
      ]
    }
  };

  return configs[type as keyof typeof configs] || configs.system;
}; 